import { UserApi } from "@studio/lib/api/user-api";
import { CreateApiKeyDto, UserRolesDto } from "@repo/api/user";
import { AmplifilesClient } from "@studio/lib/api/api-client";
import { InviteDto } from "@repo/api/invite";
import { TeamRole } from "@repo/api/team-roles";
import {
  ManualIntercomSyncResponseDto,
  UserRangeInfoDto,
  AdminUserDto,
  BillingAccountDto,
  AddCreditsResponseDto,
  UserRoleDto,
  AssignRoleResponseDto,
  OrganisationDto,
  AdminTeamDto,
  ManualIntercomSyncRequestDto,
  AdminOverlayTemplateDto,
  CopyOverlayTemplateResponseDto,
  AdminUserTeamsResponseDto,
  AdminTeamMembershipResponseDto,
  AdminInvitesResponseDto,
  AdminResendInviteResponseDto,
  AdminTeamWithMembersDto,
  AdminAddTeamMemberResponseDto,
} from "@repo/api/admin";
import {
  BlockRuleDto,
  CreateBlockRuleRequestDto,
  DeleteBlockRuleResponseDto,
  ListBlockRulesResponseDto,
} from "@repo/api/block-rule";
import {
  CreateFontResponseDto,
  DeleteFontResponseDto,
  ListFontsResponseDto,
  SeedGoogleFontsResponseDto,
  UploadCustomFontResponseDto,
} from "@repo/api/font";
import {
  PendingBackgroundMusicPeaksResponseDto,
  SaveBackgroundMusicPeaksResponseDto,
} from "@repo/api/background-music";

/**
 * Admin API wrapper that provides admin-specific functionality
 * Reuses existing API clients and adds admin-only operations
 */
export class AdminApi {
  /**
   * Create an API key for the current authenticated user
   * Note: This doesn't require superuser privileges, any authenticated user can create API keys
   */
  static async createApiKey(data: CreateApiKeyDto) {
    return UserApi.createApiKey(data);
  }

  /**
   * Get user ID range information for batch processing
   * Requires superuser privileges
   */
  static async getUserRangeInfo() {
    const response = await AmplifilesClient.instance.get("/admin/users/range");
    return UserRangeInfoDto.parse(response.data);
  }

  /**
   * Trigger manual Intercom sync with optional ID range
   * Requires superuser privileges
   */
  static async triggerIntercomSync(options?: ManualIntercomSyncRequestDto) {
    const response = await AmplifilesClient.instance.post(
      "/admin/intercom/manual-sync",
      options || {},
    );
    return ManualIntercomSyncResponseDto.parse(response.data);
  }

  /**
   * Get user by email address
   * Requires superuser privileges
   */
  static async getUserByEmail(email: string) {
    const response = await AmplifilesClient.instance.get(
      `/admin/users/by-email?email=${encodeURIComponent(email)}`,
    );
    return AdminUserDto.parse(response.data);
  }

  /**
   * Get billing accounts for a user
   * Requires superuser privileges
   */
  static async getUserBillingAccounts(userId: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/users/${userId}/billing-accounts`,
    );
    return BillingAccountDto.array().parse(response.data);
  }

  /**
   * Add credits to a billing account
   * Requires superuser privileges
   */
  static async addCredits(
    billingAccountId: number,
    credits: number,
    userEmail: string,
  ) {
    const response = await AmplifilesClient.instance.post(
      "/admin/credits/add",
      {
        billingAccountId,
        credits,
        userEmail,
      },
    );
    return AddCreditsResponseDto.parse(response.data);
  }

  /**
   * Get user's current roles
   * Requires superuser privileges
   */
  static async getUserRoles(userId: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/users/${userId}/roles`,
    );
    return UserRoleDto.array().parse(response.data);
  }

  /**
   * Assign a role to a user
   * Requires superuser privileges
   */
  static async assignRole(userId: number, role: string, userEmail: string) {
    const response = await AmplifilesClient.instance.post(
      `/admin/users/${userId}/roles`,
      {
        role,
        userEmail,
      },
    );
    return AssignRoleResponseDto.parse(response.data);
  }

  /**
   * Get current user's roles
   * Available to any authenticated user (not admin-only)
   */
  static async getUserSelfRoles() {
    const response = await AmplifilesClient.instance.get("/user/self/roles");
    return UserRolesDto.parse(response.data);
  }

  /**
   * Get all organizations for team creation
   * Requires superuser privileges
   */
  static async getAllOrganizations() {
    const response = await AmplifilesClient.instance.get(
      "/admin/organizations",
    );
    return OrganisationDto.array().parse(response.data);
  }

  /**
   * Create a new team with optional organization link
   * Requires superuser privileges
   */
  static async createTeam(params: { name: string; organisationId?: number }) {
    const response = await AmplifilesClient.instance.post(
      "/admin/teams",
      params,
    );
    return AdminTeamDto.parse(response.data);
  }

  /**
   * Get team by ID
   * Requires superuser privileges
   */
  static async getTeamById(teamId: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/teams/${teamId}`,
    );
    return AdminTeamDto.parse(response.data);
  }

  /**
   * Create an invite as admin bypassing team membership checks
   * Requires superuser privileges
   */
  static async createInviteAsAdmin(params: {
    email: string;
    role: TeamRole;
    teamId: number;
  }) {
    const response = await AmplifilesClient.instance.post(
      "/admin/invites",
      params,
    );
    return InviteDto.parse(response.data);
  }

  /**
   * Get overlay template by ID
   * Requires superuser privileges
   */
  static async getOverlayTemplateById(overlayTemplateId: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/overlay-templates/${overlayTemplateId}`,
    );
    return AdminOverlayTemplateDto.parse(response.data);
  }

  /**
   * Copy an overlay template from one team to another
   * Copies all associated media assets in S3 and creates new database records
   * Requires superuser privileges
   */
  static async copyOverlayTemplate(params: {
    sourceTeamId: number;
    targetTeamId: number;
    overlayTemplateId: number;
  }) {
    const response = await AmplifilesClient.instance.post(
      "/admin/overlay-templates/copy",
      params,
    );
    return CopyOverlayTemplateResponseDto.parse(response.data);
  }

  /**
   * Sync billing for a team
   * Requires superuser privileges
   */
  static async syncBilling(teamId: number) {
    await AmplifilesClient.instance.post("/admin/billing/sync", {
      teamId,
    });
    return { success: true };
  }

  static async getUserTeams(email: string) {
    const response = await AmplifilesClient.instance.get(
      `/admin/team-memberships/by-email?email=${encodeURIComponent(email)}`,
    );
    return AdminUserTeamsResponseDto.parse(response.data);
  }

  static async joinTeam(teamId: number) {
    const response = await AmplifilesClient.instance.post(
      "/admin/team-memberships/join",
      { teamId },
    );
    return AdminTeamMembershipResponseDto.parse(response.data);
  }

  static async leaveTeam(teamId: number) {
    const response = await AmplifilesClient.instance.post(
      "/admin/team-memberships/leave",
      { teamId },
    );
    return AdminTeamMembershipResponseDto.parse(response.data);
  }

  static async getInvitesByEmail(email: string) {
    const response = await AmplifilesClient.instance.get(
      `/admin/invites/by-email?email=${encodeURIComponent(email)}`,
    );
    return AdminInvitesResponseDto.parse(response.data);
  }

  static async resendInvite(inviteId: number) {
    const response = await AmplifilesClient.instance.post(
      "/admin/invites/resend",
      { inviteId },
    );
    return AdminResendInviteResponseDto.parse(response.data);
  }

  static async getTeamWithMembers(teamId: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/teams/${teamId}/members`,
    );
    return AdminTeamWithMembersDto.parse(response.data);
  }

  static async addTeamMember(teamId: number, email: string) {
    const response = await AmplifilesClient.instance.post(
      `/admin/teams/${teamId}/members`,
      { email },
    );
    return AdminAddTeamMemberResponseDto.parse(response.data);
  }

  static async listBlockRules() {
    const response = await AmplifilesClient.instance.get("/admin/block-rules");
    return ListBlockRulesResponseDto.parse(response.data);
  }

  static async createBlockRule(data: CreateBlockRuleRequestDto) {
    const response = await AmplifilesClient.instance.post(
      "/admin/block-rules",
      data,
    );
    return BlockRuleDto.parse(response.data);
  }

  static async deleteBlockRule(externalId: string) {
    const response = await AmplifilesClient.instance.post(
      `/admin/block-rules/${externalId}/delete`,
      {},
    );
    return DeleteBlockRuleResponseDto.parse(response.data);
  }

  static async listFonts() {
    const response = await AmplifilesClient.instance.get("/admin/fonts");
    return ListFontsResponseDto.parse(response.data);
  }

  static async createGoogleFonts(fonts: { family: string; text?: string }[]) {
    const response = await AmplifilesClient.instance.post("/admin/fonts", {
      fonts,
    });
    return CreateFontResponseDto.parse(response.data);
  }

  static async seedGoogleFonts(limit: number) {
    const response = await AmplifilesClient.instance.post("/admin/fonts/seed", {
      limit,
    });
    return SeedGoogleFontsResponseDto.parse(response.data);
  }

  static async uploadCustomFont(params: {
    teamExternalId: string;
    family: string;
    text?: string;
    files: File[];
  }) {
    const formData = new FormData();
    formData.append("teamExternalId", params.teamExternalId);
    formData.append("family", params.family);
    if (params.text) {
      formData.append("text", params.text);
    }
    for (const file of params.files) {
      formData.append("fonts", file);
    }

    const response = await AmplifilesClient.instance.post(
      "/admin/fonts/custom",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return UploadCustomFontResponseDto.parse(response.data);
  }

  static async deleteFont(params: {
    teamExternalId: string;
    fontExternalId: string;
  }) {
    const response = await AmplifilesClient.instance.delete(
      `/team/${params.teamExternalId}/font/${params.fontExternalId}`,
      {},
    );
    return DeleteFontResponseDto.parse(response.data);
  }

  static async getPendingBackgroundMusicPeaks(size: number) {
    const response = await AmplifilesClient.instance.get(
      `/admin/background-music/pending-peaks?size=${size}`,
    );
    return PendingBackgroundMusicPeaksResponseDto.parse(response.data);
  }

  static async saveBackgroundMusicPeaks(
    externalId: string,
    durationMs: number,
    peaks?: number[][],
  ) {
    const response = await AmplifilesClient.instance.post(
      "/admin/background-music/save-peaks",
      { externalId, durationMs, peaks },
    );
    return SaveBackgroundMusicPeaksResponseDto.parse(response.data);
  }
}
